/**
 * Swipeable Row Component
 * 
 * Swipe left/right to reveal actions
 * 
 * Version: 6.2.0
 */
import React, { useState, useRef, useCallback, ReactNode } from 'react'
import './SwipeableRow.css'

interface SwipeAction {
  id: string
  icon: ReactNode
  label: string
  color: string
  onClick: () => void
}

interface SwipeableRowProps {
  children: ReactNode
  leftActions?: SwipeAction[]
  rightActions?: SwipeAction[]
  threshold?: number // Percentage to trigger action (0-1)
  disabled?: boolean
  onSwipeStart?: () => void
  onSwipeEnd?: () => void
  className?: string
}

const SwipeableRow: React.FC<SwipeableRowProps> = ({
  children,
  leftActions = [],
  rightActions = [],
  threshold = 0.4,
  disabled = false,
  onSwipeStart,
  onSwipeEnd,
  className = ''
}) => {
  const [translateX, setTranslateX] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const [activeAction, setActiveAction] = useState<string | null>(null)
  const startXRef = useRef(0)
  const currentXRef = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)

  // Calculate max swipe distances
  const maxLeftSwipe = leftActions.length * 80
  const maxRightSwipe = rightActions.length * 80

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (disabled) return
    startXRef.current = e.touches[0].clientX
    currentXRef.current = startXRef.current
    setIsSwiping(true)
    onSwipeStart?.()
  }, [disabled, onSwipeStart])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isSwiping || disabled) return

    currentXRef.current = e.touches[0].clientX
    const diff = currentXRef.current - startXRef.current
    
    // Limit swipe distance
    let limitedDiff = diff
    if (diff > 0 && leftActions.length > 0) {
      limitedDiff = Math.min(diff, maxLeftSwipe)
    } else if (diff < 0 && rightActions.length > 0) {
      limitedDiff = Math.max(diff, -maxRightSwipe)
    }
    
    // Add resistance at edges
    if (diff > maxLeftSwipe) {
      limitedDiff = maxLeftSwipe + (diff - maxLeftSwipe) * 0.2
    } else if (diff < -maxRightSwipe) {
      limitedDiff = -maxRightSwipe + (diff + maxRightSwipe) * 0.2
    }

    setTranslateX(limitedDiff)
  }, [isSwiping, disabled, leftActions.length, rightActions.length, maxLeftSwipe, maxRightSwipe])

  const handleTouchEnd = useCallback(() => {
    if (!isSwiping) return
    
    const diff = currentXRef.current - startXRef.current
    const containerWidth = containerRef.current?.offsetWidth || 300
    const triggerThreshold = containerWidth * threshold
    
    // Determine if swipe was strong enough
    if (diff > 0 && diff > triggerThreshold && leftActions.length > 0) {
      // Swipe right - show left actions fully
      setTranslateX(maxLeftSwipe)
      setActiveAction(leftActions[0]?.id || null)
    } else if (diff < 0 && Math.abs(diff) > triggerThreshold && rightActions.length > 0) {
      // Swipe left - show right actions fully
      setTranslateX(-maxRightSwipe)
      setActiveAction(rightActions[0]?.id || null)
    } else {
      // Reset
      setTranslateX(0)
      setActiveAction(null)
    }
    
    setIsSwiping(false)
    onSwipeEnd?.()
  }, [isSwiping, threshold, leftActions, rightActions, maxLeftSwipe, maxRightSwipe, onSwipeEnd])

  const handleActionClick = (action: SwipeAction) => {
    action.onClick()
    setTranslateX(0)
    setActiveAction(null)
  }

  const handleContentClick = () => {
    if (translateX !== 0) {
      setTranslateX(0)
      setActiveAction(null)
    }
  }

  return (
    <div 
      ref={containerRef}
      className={`swipeable-row-container ${className}`}
    >
      {/* Left Actions (revealed on swipe right) */}
      {leftActions.length > 0 && (
        <div 
          className="swipe-actions left"
          style={{ width: `${maxLeftSwipe}px` }}
        >
          {leftActions.map((action) => (
            <button
              key={action.id}
              className={`swipe-action ${activeAction === action.id ? 'active' : ''}`}
              style={{ backgroundColor: action.color }}
              onClick={() => handleActionClick(action)}
            >
              <span className="action-icon">{action.icon}</span>
              <span className="action-label">{action.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Right Actions (revealed on swipe left) */}
      {rightActions.length > 0 && (
        <div 
          className="swipe-actions right"
          style={{ width: `${maxRightSwipe}px` }}
        >
          {rightActions.map((action) => (
            <button
              key={action.id}
              className={`swipe-action ${activeAction === action.id ? 'active' : ''}`}
              style={{ backgroundColor: action.color }}
              onClick={() => handleActionClick(action)}
            >
              <span className="action-icon">{action.icon}</span>
              <span className="action-label">{action.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div 
        className={`swipe-content ${isSwiping ? 'swiping' : ''}`}
        style={{ transform: `translateX(${translateX}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleContentClick}
      >
        {children}
      </div>
    </div>
  )
}

export default SwipeableRow